export const LENS_CONTEXT_EVENT = "owletto:lobu-lens-context";
export const LENS_CONTEXT_REQUEST_EVENT = "owletto:lobu-lens-context-request";
export const LENS_OPEN_TARGET_EVENT = "owletto:lobu-lens-open-target";
export const LENS_OPEN_TARGET_RESPONSE_EVENT =
	"owletto:lobu-lens-open-target-response";

export type LobuLensContext =
	| {
			kind: "browser";
			/** Native ambient context only knows the site, while Chrome Lens knows the page. */
			urlScope?: "page" | "origin";
			url: string | null;
			title: string | null;
			applicationName: string;
	  }
	| {
			kind: "application";
			applicationId: string | null;
			applicationName: string;
			windowTitle: string | null;
	  };

export function normalizeLensUrl(
	value: string | null | undefined,
): string | null {
	if (!value?.trim()) return null;
	try {
		const url = new URL(value);
		if (url.protocol !== "http:" && url.protocol !== "https:") return null;
		const pathname = url.pathname.replace(/\/+$/, "") || "/";
		return `${url.origin}${pathname}${url.search}`;
	} catch {
		return null;
	}
}

export function isAttentionItemRelevant(
	item: { browserUrl?: string | null },
	context: LobuLensContext | null,
): boolean {
	if (context?.kind !== "browser") return false;
	const itemUrl = normalizeLensUrl(item.browserUrl);
	const contextUrl = normalizeLensUrl(context.url);
	if (itemUrl == null || contextUrl == null) return false;
	return context.urlScope === "origin"
		? new URL(itemUrl).origin === new URL(contextUrl).origin
		: itemUrl === contextUrl;
}

/** Identity that survives title churn: the page or site for a browser, the app otherwise. */
export function lensContextScopeKey(
	context: LobuLensContext | undefined,
): string {
	if (!context) return "";
	if (context.kind === "browser") {
		return `browser:${context.urlScope ?? "page"}:${normalizeLensUrl(context.url) ?? ""}`;
	}
	return `application:${context.applicationId ?? context.applicationName}`;
}

export function parseLensEventDetail<T>(event: Event): T | null {
	if (!(event instanceof CustomEvent) || typeof event.detail !== "string") {
		return null;
	}
	try {
		return JSON.parse(event.detail) as T;
	} catch {
		return null;
	}
}

export function dispatchLensEvent(name: string, detail: unknown): void {
	document.dispatchEvent(
		new CustomEvent(name, {
			detail: JSON.stringify(detail),
		}),
	);
}

/** Only the extension parent may scope an embedded workspace. */
export function extensionAmbientContext(
	event: MessageEvent,
	parent: Window,
	self: Window,
): LobuLensContext | undefined {
	if (
		parent === self ||
		event.source !== parent ||
		!/^chrome-extension:\/\/[a-p]{32}$/.test(event.origin) ||
		event.data?.owletto !== "ambient-context"
	)
		return undefined;
	const context = event.data.context;
	if (
		context?.kind !== "browser" ||
		(context.url !== null && typeof context.url !== "string")
	)
		return undefined;
	return {
		kind: "browser",
		url: normalizeLensUrl(context.url),
		title: null,
		applicationName: "Chrome",
	};
}
